import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Spin } from 'antd';
import './Slideshow.css';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';

// Memoized configuration
const CONFIG = {
  maxImagesPerFolder: 2, // READS ONLY TWO IMAGES PER FOLDER
  imageQuality: {
    width: 600,
    height: 400,
    quality: 0.7
  },
  cacheExpiry: 10 * 60 * 1000, // 10 minutes cache
  requestTimeout: 15000 // 15 second timeout
};

export interface MediaItem {
  mediaSrc: string | File;
  mediaType: 'IMAGE' | 'VIDEO';
  description: string;
  folderUrl?: string;
  sharePointUrl?: string;
  parentFolder?: string;
}

interface SlideshowProps {
  mediaItems?: MediaItem[];
  onFolderClick?: (folderUrl: string) => void;
  siteId: string;
  driveId: string;
  folderPath: string;
  autoRotateInterval?: number;
  pauseOnHover?: boolean;
  recursive?: boolean;
  maxImagesPerFolder?: number;
}

const Slideshow: React.FC<SlideshowProps> = React.memo(({
  mediaItems: initialMediaItems = [],
  onFolderClick,
  siteId,
  driveId,
  folderPath,
  autoRotateInterval = 5000,
  pauseOnHover = true,
  recursive = true,
  maxImagesPerFolder = CONFIG.maxImagesPerFolder
}) => {
  const { instance } = useMsal();
  const [slideIndex, setSlideIndex] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [imageLoadStates, setImageLoadStates] = useState<Record<string, boolean>>({});
  const autoRotateTimerRef = useRef<NodeJS.Timeout | null>(null);
  const imageCache = useRef<Map<string, string>>(new Map());
  
  // State for both fallback and loaded media items
  const [fallbackMediaItems] = useState<MediaItem[]>(initialMediaItems);
  const [loadedMediaItems, setLoadedMediaItems] = useState<MediaItem[]>([]);
  const [isLoadingSharePoint, setIsLoadingSharePoint] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [currentFolder, setCurrentFolder] = useState<string>(folderPath);

  // Memoized cache key for better cache management
  const cacheKey = useMemo(() => 
    `slideshow_${siteId}_${driveId}_${currentFolder}_${maxImagesPerFolder}_${recursive}`, 
    [siteId, driveId, currentFolder, maxImagesPerFolder, recursive]
  );

  // Determine which media items to display
  const mediaItems = useMemo(() => 
    loadedMediaItems.length > 0 ? loadedMediaItems : fallbackMediaItems,
    [loadedMediaItems, fallbackMediaItems]
  );
  
  const getAccessToken = useCallback(async () => {
    try {
      const accounts = instance.getAllAccounts();
      if (!accounts[0]) throw new Error('No account found');
      
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      });
      
      return response.accessToken;
    } catch (silentError) {
      try {
        const accounts = instance.getAllAccounts();
        if (!accounts[0]) throw new Error('No account found');
        
        const response = await instance.acquireTokenSilent({
          scopes: ["User.Read", 'Sites.Read.All'],
          account: accounts[0]
        });
        
        return response.accessToken;
      } catch (error) {
        console.error('Error acquiring token:', error);
        throw error;
      }
    }
  }, [instance]);

  // Preload images for better performance
  const preloadImages = useCallback((items: MediaItem[]) => {
    items.forEach((item, index) => {
      if (item.mediaType === 'IMAGE') {
        const img = new Image();
        img.onload = () => {
          setImageLoadStates(prev => ({ ...prev, [`${index}`]: true }));
        };
        img.src = typeof item.mediaSrc === 'string' ? item.mediaSrc : URL.createObjectURL(item.mediaSrc);
      }
    });
  }, []);

  // Optimized function to compress an image with caching
  const compressImage = useCallback(async (url: string): Promise<string> => {
    // Check cache first
    if (imageCache.current.has(url)) {
      return imageCache.current.get(url)!;
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          resolve(url);
          return;
        }
  
        // Calculate dimensions maintaining aspect ratio
        const aspectRatio = img.width / img.height;
        const targetWidth = CONFIG.imageQuality.width;
        const targetHeight = CONFIG.imageQuality.height;
        
        let newWidth = targetWidth;
        let newHeight = targetHeight;
  
        // Only resize if the image is larger than target dimensions
        if (img.width > targetWidth || img.height > targetHeight) {
          if (aspectRatio > 1) {
            newHeight = Math.round(targetWidth / aspectRatio);
          } else {
            newWidth = Math.round(targetHeight * aspectRatio);
          }
        } else {
          // If image is smaller, use original dimensions
          newWidth = img.width;
          newHeight = img.height;
        }
  
        canvas.width = newWidth;
        canvas.height = newHeight;
  
        // Draw image with high quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, newWidth, newHeight);
        
        // Convert to compressed JPEG
        const compressedUrl = canvas.toDataURL('image/jpeg', CONFIG.imageQuality.quality);
        
        // Cache the result
        imageCache.current.set(url, compressedUrl);
        
        resolve(compressedUrl);
      };
  
      img.onerror = () => {
        console.warn('Image loading failed, using original URL');
        resolve(url);
      };
  
      img.src = url;
    });
  }, []);
  
  // Process a single image item
  const processImageItem = useCallback(async (file: any, folderPath: string) => {
    try {
      let imageUrl;
      
      // Get image URL from SharePoint
      if (!file["@microsoft.graph.downloadUrl"]) {
        const token = await getAccessToken();
        
        const fileResponse = await axios.get(
          `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${file.id}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            params: {
              select: '@microsoft.graph.downloadUrl,webUrl,name',
              expand: 'thumbnails(select=medium)'
            }
          }
        );
        
        imageUrl = fileResponse.data["@microsoft.graph.downloadUrl"];
      } else {
        imageUrl = file["@microsoft.graph.downloadUrl"];
      }
  
      // Compress the image
      const compressedImageUrl = await compressImage(imageUrl);
      
      return {
        mediaSrc: compressedImageUrl,
        mediaType: 'IMAGE',
        description: `${file.name.split('.')[0]} ${folderPath !== currentFolder ? `(${folderPath.split('/').pop()})` : ''}`,
        sharePointUrl: file.webUrl,
        parentFolder: folderPath
      } as MediaItem;
    } catch (fileError) {
      console.error(`Error processing image ${file.name}:`, fileError);
      return null;
    }
  }, [siteId, driveId, currentFolder, getAccessToken, compressImage]);

  // Fetch all contents from a folder with comprehensive caching
  const fetchFolderContents = useCallback(async (folder: string) => {
    setIsLoadingSharePoint(true);
    setError(null);
    
    try {
      // Check cache with timestamp
      const cachedData = localStorage.getItem(cacheKey);
      const cacheTimestamp = localStorage.getItem(`${cacheKey}_timestamp`);
      const now = Date.now();
      const isCacheValid = cacheTimestamp && (now - parseInt(cacheTimestamp)) < CONFIG.cacheExpiry;
      
      if (cachedData && isCacheValid) {
        const parsedData = JSON.parse(cachedData);
        setLoadedMediaItems(parsedData);
        preloadImages(parsedData);
        setIsLoadingSharePoint(false);
        return;
      }
      
      const token = await getAccessToken();
      const endpoint = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/root:${folder}:/children`;
      
      const response = await axios.get(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        timeout: CONFIG.requestTimeout
      });
      
      // Filter for image files only
      const imageFiles = response.data.value.filter((item: any) => 
        item.file && 
        (item.name.toLowerCase().endsWith('.jpg') || 
         item.name.toLowerCase().endsWith('.jpeg') || 
         item.name.toLowerCase().endsWith('.png') || 
         item.name.toLowerCase().endsWith('.gif'))
      );
      
      // Limit images per folder
      const limitedImageFiles = imageFiles.slice(0, maxImagesPerFolder);
      
      // Process images from current folder
      const imagePromises = limitedImageFiles.map((file: any) => processImageItem(file, folder));
      const processedImages = await Promise.all(imagePromises);
      const validImages = processedImages.filter(img => img !== null) as MediaItem[];
      
      // Process subfolders if recursive is enabled
      let allImages = [...validImages];
      
      if (recursive) {
        const folders = response.data.value.filter((item: any) => item.folder);
        
        for (const subfolder of folders) {
          const subfolderPath = `${folder}/${subfolder.name}`;
          const subfolderToken = await getAccessToken();
          const subfolderEndpoint = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/root:${subfolderPath}:/children`;
          
          try {
            const subfolderResponse = await axios.get(subfolderEndpoint, {
              headers: {
                'Authorization': `Bearer ${subfolderToken}`,
                'Accept': 'application/json'
              },
              timeout: CONFIG.requestTimeout
            });
            
            const subfolderImageFiles = subfolderResponse.data.value.filter((item: any) => 
              item.file && 
              (item.name.toLowerCase().endsWith('.jpg') || 
               item.name.toLowerCase().endsWith('.jpeg') || 
               item.name.toLowerCase().endsWith('.png') || 
               item.name.toLowerCase().endsWith('.gif'))
            );
            
            // Limit images per subfolder
            const limitedSubfolderImageFiles = subfolderImageFiles.slice(0, maxImagesPerFolder);
            
            const subfolderImagePromises = limitedSubfolderImageFiles.map((file: any) => 
              processImageItem(file, subfolderPath)
            );
            
            const subfolderProcessedImages = await Promise.all(subfolderImagePromises);
            const subfolderValidImages = subfolderProcessedImages.filter(img => img !== null) as MediaItem[];
            
            allImages = [...allImages, ...subfolderValidImages];
          } catch (subfolderError) {
            console.error(`Error fetching subfolder ${subfolderPath}:`, subfolderError);
          }
        }
      }
      
      if (allImages.length > 0) {
        setLoadedMediaItems(allImages);
        // Cache the results
        localStorage.setItem(cacheKey, JSON.stringify(allImages));
        localStorage.setItem(`${cacheKey}_timestamp`, now.toString());
        // Preload images
        preloadImages(allImages);
      } else {
        // If no SharePoint images were found, we'll keep using the fallback images
        console.log('No SharePoint images found, using fallback images');
      }
    } catch (err) {
      console.error('Error fetching SharePoint folder contents:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch SharePoint images'));
      
      // Try to use stale cache as fallback
      const staleCache = localStorage.getItem(cacheKey);
      if (staleCache) {
        const parsedStaleData = JSON.parse(staleCache);
        setLoadedMediaItems(parsedStaleData);
        preloadImages(parsedStaleData);
        console.log('Using stale cache data due to fetch error');
      }
    } finally {
      setIsLoadingSharePoint(false);
    }
  }, [siteId, driveId, processImageItem, recursive, getAccessToken, maxImagesPerFolder, cacheKey, preloadImages]);

  // Navigate to a different folder
  const navigateToFolder = useCallback((folderUrl: string) => {
    setCurrentFolder(folderUrl);
  }, []);

  // Navigate through slides
  const plusSlides = (n: number) => {
    setSlideIndex((prevIndex) => {
      let newIndex = prevIndex + n;
      if (newIndex > mediaItems.length) newIndex = 1;
      if (newIndex < 1) newIndex = mediaItems.length;
      return newIndex;
    });
  };

  // Load images when currentFolder changes
  useEffect(() => {
    fetchFolderContents(currentFolder);
    
    return () => {
      if (autoRotateTimerRef.current) {
        clearInterval(autoRotateTimerRef.current);
        autoRotateTimerRef.current = null;
      }
    };
  }, [currentFolder, fetchFolderContents]);

  // Auto-rotate slides
  useEffect(() => {
    if (autoRotateTimerRef.current) {
      clearInterval(autoRotateTimerRef.current);
    }
    
    if (mediaItems.length > 1 && !isPaused) {
      autoRotateTimerRef.current = setInterval(() => {
        plusSlides(1);
      }, autoRotateInterval);
    }

    return () => {
      if (autoRotateTimerRef.current) {
        clearInterval(autoRotateTimerRef.current);
        autoRotateTimerRef.current = null;
      }
    };
  }, [mediaItems, autoRotateInterval, isPaused]);

  // Reset slide index when media items change
  useEffect(() => {
    setSlideIndex(1);
  }, [mediaItems]);

  // Get media source URL
  const getMediaSrc = useCallback((mediaSrc: string | File) => {
    if (typeof mediaSrc === 'string') {
      return mediaSrc;
    } else {
      return URL.createObjectURL(mediaSrc);
    }
  }, []);

  // Handle clicking on an image
  const handleImageClick = useCallback((item: MediaItem) => {
    if (item.parentFolder && item.sharePointUrl) {
      // Extract the parent folder URL from the image URL
      try {
        // SharePoint URLs typically have a format that includes the folder path
        // We'll extract the folder path by removing everything after the last slash in the path
        const urlObj = new URL(item.sharePointUrl);
        const pathParts = urlObj.pathname.split('/');
        
        // Remove the file name (last part) from the path
        pathParts.pop();
        const folderPath = pathParts.join('/');
        
        // Reconstruct the URL with the folder path
        const folderUrl = `${urlObj.origin}${folderPath}`;
        window.open(folderUrl, '_blank');
      } catch (error) {
        // Fallback to the sharePoint URL if we can't parse it
        console.error('Error extracting folder URL:', error);
        window.open(item.sharePointUrl, '_blank');
      }
    } else if (item.sharePointUrl) {
      // If we can't determine the parent folder, use the direct SharePoint URL
      window.open(item.sharePointUrl, '_blank');
    } else if (item.folderUrl) {
      // If there's a specific folderUrl provided, use that
      if (onFolderClick) {
        onFolderClick(item.folderUrl);
      } else {
        navigateToFolder(item.folderUrl);
      }
    }
  }, [onFolderClick, navigateToFolder]);

  // Handle mouse events for pausing
  const handleMouseEnter = useCallback(() => {
    if (pauseOnHover) {
      setIsPaused(true);
    }
  }, [pauseOnHover]);

  const handleMouseLeave = useCallback(() => {
    if (pauseOnHover) {
      setIsPaused(false);
    }
  }, [pauseOnHover]);
  
  // Add hover effect styles - placed here following hooks rules
  useEffect(() => {
    // Add a style tag for the hover effect
    const styleTag = document.createElement('style');
    styleTag.innerHTML = `
      .slideshow-container:hover .hover-overlay {
        opacity: 1 !important;
      }
    `;
    document.head.appendChild(styleTag);
    
    return () => {
      // Clean up
      document.head.removeChild(styleTag);
    };
  }, []);

  // Show loading state only during initial load
  if ((mediaItems.length === 0) && isLoadingSharePoint) {
    return (
      <div className="slideshow-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '230px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // If no images at all, show message
  if (mediaItems.length === 0) {
    return (
      <div className="slideshow-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '230px' }}>
        <p>No images available</p>
      </div>
    );
  }

  return (
    <div 
      className="slideshow-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {mediaItems.map((item, index) => (
        <div
          key={index}
          className="mySlides fade"
          style={{ 
            display: slideIndex === index + 1 ? 'block' : 'none',
            height: '14.4rem',
            position: 'relative'
          }}
        >
          {item.mediaType === 'IMAGE' ? (
            <>
              <img 
                src={getMediaSrc(item.mediaSrc)} 
                alt={item.description || 'Image'}
                onClick={() => handleImageClick(item)}
                loading="lazy"
                decoding="async"
                style={{
                  width: '100%',   
                  height: '14.4rem',
                  borderRadius: '10px',
                  cursor: (item.folderUrl || item.sharePointUrl) ? 'pointer' : 'default',
                  objectFit: 'cover',
                  willChange: 'transform',
                  backfaceVisibility: 'hidden',
                  opacity: imageLoadStates[`${index}`] ? 1 : 0.7,
                  transition: 'opacity 0.3s ease'
                }}
                onError={(e) => {
                  console.error('Image failed to load:', item.mediaSrc);
                  e.currentTarget.style.opacity = '0.5';
                }}
              />
              {/* Display a small loading indicator if we're still loading SharePoint images */}
              {isLoadingSharePoint && mediaItems === fallbackMediaItems && (
                <div style={{ 
                  position: 'absolute', 
                  top: '5px', 
                  right: '5px',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  borderRadius: '50%',
                  padding: '3px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px'
                }}>
                  
                </div>
              )}
            </>
          ) : (
            <video 
              src={getMediaSrc(item.mediaSrc)} 
              controls 
              style={{ width: '100%' }} 
            />
          )}
          
          {/* Blurred overlay at the bottom with standardized message - only visible on hover */}
          <div 
  className="hover-overlay"
  style={{ 
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '30%', 
    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0) 100%)',
    backdropFilter: 'blur(2px)',
    borderBottomLeftRadius: '10px',
    borderBottomRightRadius: '10px',
    display: 'flex',
    flexDirection: 'column',  // Stack texts vertically
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    opacity: 0,
    transition: 'opacity 0.3s ease'
  }}
>
  <div style={{ color: 'white', fontSize: '0.8em', fontWeight: 'bold', marginBottom: '4px' }}>
    Click to View
  </div>
  
  <div style={{ color: 'white', fontSize: '1.0em', fontWeight: 'bold' }}>
    Picture gallery of company-wide recognition activities, celebrations.
  </div>
</div>

        </div>
      ))}
      
      {mediaItems.length > 1 && (
        <>
          <a className="prev" onClick={() => plusSlides(-1)}>❮</a>
          <a className="next" onClick={() => plusSlides(1)}>❯</a>
        </>
      )}
    </div>
  );
});

export default Slideshow;